import React, { useContext, useState, useEffect, createContext } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { db } from '../firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const auth = getAuth();

  // Helper function to check if user is SuperAdmin
  const isSuperAdmin = () => {
    return (
      currentUser?.userType?.includes('SuperAdmin') ||
      currentUser?.isSuperAdmin === true
    );
  };

  // Helper function to check if user is AppAdmin
  const isAppAdmin = () => {
    return currentUser?.userType?.includes('AppAdmin') || false;
  };

  // Helper function to check specific permissions
  const hasPermission = (permission) => {
    if (isSuperAdmin()) return true; // SuperAdmin has all permissions
    
    // For future use when we implement more granular permissions
    const userType = currentUser?.userType?.[0];
    if (!userType) return false;

    // TODO: Implement permission checking against userTypes collection
    return false;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          let userDoc = await getDoc(userDocRef);

          // Retry for new signups: the Firestore doc may not exist yet due to
          // a race between onAuthStateChanged and the signup handler writing the doc.
          if (!userDoc.exists()) {
            for (let attempt = 0; attempt < 3; attempt++) {
              await new Promise(r => setTimeout(r, 1000)); // wait 1 second
              userDoc = await getDoc(userDocRef);
              if (userDoc.exists()) break;
            }
          }

          if (userDoc.exists()) {
            // Fetch the user's organization memberships from the subcollection
            const orgsCollectionRef = collection(db, 'users', user.uid, 'organizations');
            const orgsSnapshot = await getDocs(orgsCollectionRef);
            const organizations = orgsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const userData = userDoc.data();
            
            // For existing users, if onboardingComplete is not set, default to true
            // (they're existing users, so they've already been using the app)
            const onboardingComplete = userData?.onboardingComplete !== undefined 
              ? userData.onboardingComplete 
              : true; // Default to true for existing users
            const onboardingStep = userData?.onboardingStep !== undefined 
              ? userData.onboardingStep 
              : 4; // Default to completed for existing users

            // Migration: Handle missing fields for existing users
            const needsMigration = !userData?.appID || !userData?.role || !userData?.status || !userData?.activeOrgs;
            
            if (needsMigration) {
              console.log('Web AuthContext: User document needs migration, applying defaults...');
              const migrationUpdates = {};
              
              // Generate appID if missing (critical field)
              if (!userData?.appID) {
                try {
                  // Import dynamically to avoid circular dependencies
                  const { generateVanityAppId } = await import('../utils/vanityAppId');
                  migrationUpdates.appID = await generateVanityAppId();
                  console.log('Web AuthContext: Generated appID for existing user:', migrationUpdates.appID);
                } catch (error) {
                  console.error('Web AuthContext: Failed to generate appID, using uid as fallback:', error);
                  migrationUpdates.appID = user.uid; // Fallback
                }
              }
              
              // Set defaults for missing fields
              if (!userData?.role) {
                migrationUpdates.role = '/roles/discipler';
              }
              if (!userData?.status) {
                migrationUpdates.status = 'Active';
              }
              if (!userData?.activeOrgs) {
                migrationUpdates.activeOrgs = [];
              }
              
              // Apply migration updates (non-blocking - don't wait for it)
              if (Object.keys(migrationUpdates).length > 0) {
                import('../firebase').then(({ db }) => {
                  import('firebase/firestore').then(({ doc, updateDoc }) => {
                    updateDoc(doc(db, 'users', user.uid), migrationUpdates)
                      .then(() => {
                        console.log('Web AuthContext: Migration updates applied successfully');
                      })
                      .catch((error) => {
                        console.error('Web AuthContext: Failed to apply migration updates:', error);
                      });
                  });
                });
              }
            }

            // Validation: Ensure critical fields exist
            // If appID is missing even after migration attempt, use uid as fallback
            const validatedAppID = userData?.appID || user.uid;
            // If name is missing, use email as fallback
            const validatedName = userData?.name || user.displayName || user.email || 'User';

            setCurrentUser({
              ...user, // Auth data (uid, email, displayName, photoURL, etc.)
              ...userData, // Firestore data (includes all fields from document)
              id: userDoc.id, // Firestore document ID
              appID: validatedAppID, // Ensure appID is explicitly included with fallback
              name: validatedName, // Ensure name is explicitly included with fallback
              organizations: organizations, // Add the organizations array
              onboardingComplete, // Explicitly set onboardingComplete with default
              onboardingStep, // Explicitly set onboardingStep with default
              // Unified user document fields - ensure these are included with defaults
              role: userData?.role || '/roles/discipler',
              status: userData?.status || 'Active',
              activeOrgs: userData?.activeOrgs || [],
            });
          } else {
            console.error('User document does not exist in Firestore.');
            // New user - set basic defaults
            setCurrentUser({
              ...user,
              onboardingComplete: false,
              onboardingStep: 0,
            });
          }
        } catch (error) {
          console.error('Error fetching user document from Firestore:', error);
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
      }
      setAuthLoading(false);
    });

    return unsubscribe;
  }, [auth]);

  const logout = () => {
    return signOut(auth);
  };

  const value = {
    currentUser,
    logout,
    setCurrentUser,
    isSuperAdmin,     // Add these helper functions
    isAppAdmin,       // to the context value
    hasPermission,
    authLoading, // Expose as authLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {!authLoading && children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
